// Results.tsx
// Full detail view for a single paper evaluation.
// Shows verdict, 30-second take, reading time estimate, stats, and dimension scores.
// Supports export to Markdown and CSV.

import React, { useState } from 'react';
import type { EvaluationResult, Priority, Theme } from '../types';
import {
  VERDICT_CONFIG,
  WHAT_TO_READ_LABELS,
  PAPER_TYPE_LABELS,
  PRIORITY_OPTIONS,
} from '../utils/formatters';
import {
  estimateReadingTime,
  exportSingleMarkdown,
  exportSingleCSV,
  exportObsidianVault,
  exportNotionCSV,
} from '../utils/export';
import DimensionRow from './DimensionRow';
import ThemeToggle from './ThemeToggle';
import shared from '../styles/shared.module.css';
import resultsStyles from '../styles/Results.module.css';

interface ResultsProps {
  result: EvaluationResult;
  onReset: () => void;
  onReEvaluate: (priority: Priority) => void;
  onBack?: () => void;
  theme: Theme;
  onToggleTheme: () => void;
}

export default function Results({
  result,
  onReset,
  onReEvaluate,
  onBack,
  theme,
  onToggleTheme,
}: ResultsProps) {
  const [exportOpen, setExportOpen] = useState(false);
  const v = VERDICT_CONFIG[result.verdict];
  const readingTime = estimateReadingTime(result);

  const verdictStyle = {
    background: `var(--verdict-${v.varPrefix}-bg)`,
    border: `1px solid var(--verdict-${v.varPrefix}-border)`,
    color: `var(--verdict-${v.varPrefix})`,
  };

  return (
    <div className={resultsStyles.page}>
      <div className={resultsStyles.container}>

        <div className={resultsStyles.header}>
          <h1 className={resultsStyles.headerTitle}>Desk Reject</h1>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>

            {/* Export dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                className={resultsStyles.btnReset}
                onClick={() => setExportOpen(o => !o)}
              >
                ↓ Export ▾
              </button>
              {exportOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 4,
                    background: 'var(--bg-card)',
                    border: '1px solid var(--bg-card-border)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-lift)',
                    minWidth: 180,
                    zIndex: 100,
                    overflow: 'hidden',
                  }}
                  onMouseLeave={() => setExportOpen(false)}
                >
                  {[
                    { key: 'markdown', icon: '📄', label: 'Markdown' },
                    { key: 'obsidian', icon: '🔮', label: 'Obsidian Vault' },
                    { key: 'notion', icon: '◻️', label: 'Notion CSV' },
                    { key: 'csv', icon: '📊', label: 'CSV' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={async () => {
                        setExportOpen(false);
                        if (opt.key === 'markdown') exportSingleMarkdown(result);
                        if (opt.key === 'obsidian') await exportObsidianVault([result]);
                        if (opt.key === 'notion') exportNotionCSV([result]);
                        if (opt.key === 'csv') exportSingleCSV(result);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        padding: '10px 14px',
                        background: 'none',
                        border: 'none',
                        borderBottom: '1px solid var(--bg-card-border)',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 12,
                        color: 'var(--text-primary)',
                        textAlign: 'left',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--accent-bg)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                    >
                      <span>{opt.icon}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {onBack && (
              <button className={resultsStyles.btnReset} onClick={onBack}>
                ← Dashboard
              </button>
            )}
            <button className={resultsStyles.btnReset} onClick={onReset}>
              + Evaluate another
            </button>
          </div>
        </div>

        <p className={resultsStyles.filename}>{result.filename}</p>

        {/* Detection context badges */}
        <div className={resultsStyles.badges}>
          <span className={shared.badge}>
            Detected: {PAPER_TYPE_LABELS[result.detected_paper_type]}
            {result.detected_paper_type_confidence === 'low' ? ' (uncertain)' : ''}
          </span>
          <span className={shared.badge}>
            Weighted for: {PRIORITY_OPTIONS.find(p => p.value === result.priority)?.label}
          </span>
          <span className={shared.badge}>
            ⏱ {readingTime.hoursLabel} to read
          </span>
        </div>

        <p className={resultsStyles.detectionReasoning}>
          {result.detected_paper_type_reasoning}
        </p>

        <div className={resultsStyles.grid}>

          {/* LEFT COLUMN */}
          <div className={resultsStyles.leftCol}>

            <div
              className={`${shared.card} ${resultsStyles.verdictCard}`}
              style={verdictStyle}
            >
              <div>
                <span
                  className={resultsStyles.verdictLabel}
                  style={{ color: `var(--verdict-${v.varPrefix})` }}
                >
                  Verdict
                </span>
                <div
                  className={resultsStyles.verdictText}
                  style={{ color: `var(--verdict-${v.varPrefix})` }}
                >
                  {v.label}
                </div>
              </div>
              <div>
                <div
                  className={resultsStyles.overallLabel}
                  style={{ color: `var(--verdict-${v.varPrefix})` }}
                >
                  Overall
                </div>
                <div
                  className={resultsStyles.overallScore}
                  style={{ color: `var(--verdict-${v.varPrefix})` }}
                >
                  {result.overall.toFixed(1)}
                  <span className={resultsStyles.overallDenom}>/5</span>
                </div>
              </div>
            </div>

            <div className={shared.card}>
              <span className={shared.label}>30-second take</span>
              <p className={resultsStyles.takeText}>{result.thirty_second_take}</p>
              <div className={resultsStyles.whatToRead}>
                What to read:{' '}
                <span className={resultsStyles.whatToReadValue}>
                  {WHAT_TO_READ_LABELS[result.what_to_read]}
                </span>
                {' — '}
                {result.what_to_read_reasoning}
              </div>
              <div style={{
                marginTop: 10,
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--accent)',
                fontWeight: 600,
              }}>
                ⏱ Estimated reading time: {readingTime.hoursLabel}
              </div>
            </div>

            <div className={shared.card}>
              <span className={shared.label}>Paper stats</span>
              <div className={resultsStyles.statsGrid}>
                {[
                  { label: 'Pages', value: result.stats.page_count },
                  { label: 'Words', value: result.stats.word_count.toLocaleString() },
                  { label: 'Equations', value: result.stats.equation_count },
                  { label: 'References', value: result.stats.reference_count },
                ].map(({ label, value }) => (
                  <div key={label} className={resultsStyles.statCard}>
                    <span className={resultsStyles.statValue}>{value}</span>
                    <span className={resultsStyles.statLabel}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={shared.card}>
              <span className={shared.label}>Re-evaluate with different focus</span>
              {PRIORITY_OPTIONS
                .filter(opt => opt.value !== result.priority)
                .map(opt => (
                  <div
                    key={opt.value}
                    className={resultsStyles.reEvalOption}
                    onClick={() => onReEvaluate(opt.value)}
                  >
                    <div>
                      <div className={resultsStyles.reEvalLabel}>{opt.label}</div>
                      <div className={resultsStyles.reEvalDesc}>{opt.description}</div>
                    </div>
                    <span className={resultsStyles.reEvalArrow}>→</span>
                  </div>
                ))}
            </div>

          </div>

          {/* RIGHT COLUMN */}
          <div className={resultsStyles.rightCol}>
            <div className={shared.card}>
              <span className={shared.label}>Breakdown</span>
              <DimensionRow label="Readability" data={result.readability} />
              <DimensionRow label="Technical Rigor" data={result.technical_rigor} />
              <DimensionRow label="Novelty" data={result.novelty} />
              <DimensionRow label="Writing Quality" data={result.writing_quality} />
              <DimensionRow label="Vocabulary Profile" data={result.vocabulary_profile} />
              <DimensionRow label="Signal to Noise" data={result.signal_to_noise} />
            </div>
          </div>

        </div>
      </div>
      <ThemeToggle theme={theme} onToggle={onToggleTheme} />
    </div>
  );
}
